import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FadeUp, Eyebrow } from "./motion";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Logistics & Warehousing",
  "Healthcare & Caregiving",
  "Manufacturing & Assembly",
  "Construction & Field",
  "Other",
];

const fieldCls =
  "bg-[#0a0a0a] border-[#27272a] text-[#f5f5f5] rounded-none h-12 focus-visible:ring-1 focus-visible:ring-[#ff4500] focus-visible:border-[#ff4500] placeholder:text-[#52525b]";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in your name, email and company.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: form.company,
        industry: form.industry || null,
        phone: form.phone || null,
        message: form.message || null,
      });
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", industry: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-[#121212] border-t border-[#27272a]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <FadeUp>
              <Eyebrow num="05">Request a Demo</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#f5f5f5] mt-6 leading-[1.02]">
                See it on your floor.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-lg leading-relaxed text-[#a1a1aa] mt-6 max-w-md">
                Tell us about your team and workload. We'll arrange a hands-on demonstration
                and a fit assessment for your environment.
              </p>
              <div className="mt-10 space-y-4 font-mono-accent text-sm text-[#a1a1aa]">
                <p><span className="text-[#ff4500]">→</span> B2B pilots & bulk deployment</p>
                <p><span className="text-[#ff4500]">→</span> On-site fit & safety assessment</p>
                <p><span className="text-[#ff4500]">→</span> ROI & injury-reduction modelling</p>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <form data-testid="contact-form" onSubmit={submit} className="border border-[#27272a] bg-[#0a0a0a] p-6 sm:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">Full name *</Label>
                    <Input id="name" data-testid="contact-name" className={`${fieldCls} mt-2`} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">Work email *</Label>
                    <Input id="email" type="email" data-testid="contact-email" className={`${fieldCls} mt-2`} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@company.com" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">Company *</Label>
                    <Input id="company" data-testid="contact-company" className={`${fieldCls} mt-2`} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Logistics" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">Phone</Label>
                    <Input id="phone" data-testid="contact-phone" className={`${fieldCls} mt-2`} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 ..." />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">Industry</Label>
                    <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                      <SelectTrigger data-testid="contact-industry" className={`${fieldCls} mt-2`}>
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272a] text-[#f5f5f5] rounded-none">
                        {INDUSTRIES.map((ind) => (
                          <SelectItem key={ind} value={ind} data-testid={`industry-option-${ind}`} className="focus:bg-[#1a1a1a] focus:text-[#ff4500]">
                            {ind}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="message" className="font-mono-accent text-xs uppercase tracking-wider text-[#a1a1aa]">How can we help?</Label>
                    <Textarea id="message" data-testid="contact-message" rows={4} className={`${fieldCls} h-auto mt-2 py-3`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Team size, typical tasks, number of units..." />
                  </div>
                </div>

                <motion.button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.97 }}
                  className="mt-8 w-full sm:w-auto bg-[#ff4500] text-white px-10 py-4 font-mono-accent text-sm tracking-wide hover:bg-[#e03e00] transition-colors duration-200 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Request a Demo →"}
                </motion.button>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
